import { useCallback, useState } from 'react'
import { hidePendoGuides } from '../utils/pendo'

const SLOW_TIMER = 1000
const FAST_TIMER = 300

interface ActiveGuideType {
	guide: { launchMethod?: string }
	step: { guideId: string; backdrop?: string; hasEscapeListener: boolean }
	stepIndex: number
}
export interface PendoGuideType {
	stopGuides: () => void
	getActiveGuide: () => ActiveGuideType | undefined
	findGuideById: (guideId: string) => {
		step: {
			id: string
		}
		getTotalSteps: () => number
	}
	initialize: (evt: any) => void
	startGuides: () => void
	hideGuides: (evt: any) => void
	events: {
		on: (evt: any, cb: any) => void
	}
}

export interface PendoActionType {
	onHideCallback: () => void
}

const usePendoAction = (props: PendoActionType) => {
	const { onHideCallback } = props
	const [isPendoActive, setPendoActive] = useState<boolean>(true)
	const [intervalId, setIntervalId] = useState<NodeJS.Timeout | undefined>(
		undefined
	)
	const [intervalTimer, setIntervalTimer] = useState<number>(SLOW_TIMER)

	const pendo = window?.pendo as PendoGuideType
	const [activeGuide, setActiveGuide] = useState<ActiveGuideType | undefined>(
		pendo?.getActiveGuide?.()
	)

	const isGuideTypeBanner = (localActiveGuide: ActiveGuideType | undefined) => {
		if (!localActiveGuide) return undefined
		// If it has backdrop - meaning it is a modal or walkthrough
		if (localActiveGuide?.step?.backdrop) return false
		// Generally: hasEscapeListener is true for modals and false for banners
		return !localActiveGuide?.step?.hasEscapeListener
	}

	const isGuideLaunchMethodBadge = (
		localActiveGuide: ActiveGuideType | undefined
	) => {
		try {
			return localActiveGuide?.guide?.launchMethod?.toLowerCase() === 'badge'
		} catch (ex) {
			console.error('Error in isGuideLaunchMethodBadge:', ex)
			return false
		}
	}
	const execHideOperations = () => {
		setPendoActive(false)
		hidePendoGuides()
		onHideCallback()
	}

	const getActiveGuideStepId = () => {
		const localActiveGuide = pendo?.getActiveGuide?.()
		setActiveGuide(localActiveGuide)
		const guideStepId = localActiveGuide?.step?.guideId
		const isBanner = isGuideTypeBanner(localActiveGuide)
		const isLaunchMethodBadge = isGuideLaunchMethodBadge(localActiveGuide)
		if (isBanner || isLaunchMethodBadge) {
			console.log(
				`Enabling Modal as the predicted guide is Banner ${isBanner} or launch method  is badge ${isLaunchMethodBadge}`
			)
			execHideOperations()
			clearInterval(intervalId)
			return
		}
		if (guideStepId) {
			setPendoActive(true)
			return pendo?.findGuideById(guideStepId)
		}
		console.log('No active guide found by hook! Stopping Pendo guides..')
		execHideOperations()
		clearInterval(intervalId)
		return undefined
	}

	/**
	 * Adaptive Polling:  If the guide is the multi-stepped guide, then the interval timer
	 * will be updated based on the progress of the guide.
	 */
	const updateIntervalTimer = () => {
		// @ts-ignore
		const guideId = activeGuide?.step?.guideId
		if (guideId) {
			const guideInfo = pendo?.findGuideById(guideId)
			const totalSteps = guideInfo?.getTotalSteps?.() || 0
			if (totalSteps > 0) {
				const activeIndex = activeGuide?.stepIndex || 0
				const progressRatio = activeIndex / totalSteps
				// The update interval decreases linearly from SLOW_TIMER to FAST_TIMER as progressRatio increases from 0 to 1.
				// The smallest updateInterval can get is 300 (when progressRatio is 1), it will never be negative.
				const updateInterval =
					SLOW_TIMER - progressRatio * (SLOW_TIMER - FAST_TIMER)
				setIntervalTimer(updateInterval)
				if (updateInterval !== intervalTimer) {
					clearInterval(intervalId)
				}
			}
		}
	}

	const pollActiveGuide = useCallback(() => {
		let localIntervalId: NodeJS.Timer
		const fetchGuide = () => {
			const activeGuide = getActiveGuideStepId()
			updateIntervalTimer()
			if (activeGuide === undefined) {
				clearInterval(localIntervalId)
			}
		}
		localIntervalId = setInterval(fetchGuide, intervalTimer)
		setIntervalId(localIntervalId)
		return () => clearInterval(localIntervalId)
	}, [activeGuide?.step?.guideId])

	const startPendoGuidesPolling = () => {
		pollActiveGuide()
	}

	return {
		startPendoGuidesPolling,
		isPendoActive,
	}
}

export default usePendoAction
